"use client";

import React, { useState, useEffect, useRef } from "react";
import { MessageSquare, X, Send, Bot, User, Loader2 } from "lucide-react";
import { createClient } from "@/lib/supabase/client";

interface Message {
  id: string;
  sender: "user" | "bot";
  text: string;
  timestamp: Date;
}

export default function ChatbotWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "welcome",
      sender: "bot",
      text: "¡Hola! Soy el asistente analítico de INNI. ¿En qué puedo ayudarte hoy con la base de datos o proyectos?",
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Generar un ID de sesión único para el chat
  useEffect(() => {
    setSessionId(`sess_${Math.random().toString(36).substring(2, 9)}`);
  }, []);

  // Verificar si el usuario está autenticado
  useEffect(() => {
    const checkAuth = async () => {
      const supabase = createClient();
      const { data: { session } } = await supabase.auth.getSession();
      setIsAuthenticated(!!session);
    };
    checkAuth();
  }, []);

  // Scroll al final de los mensajes
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessageText = input.trim();
    setInput("");
    
    const userMessage: Message = {
      id: Math.random().toString(),
      sender: "user",
      text: userMessageText,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);

    try {
      const response = await fetch("/api/chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message: userMessageText,
          sessionId: sessionId,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Error en la respuesta del servidor");
      }

      const botMessage: Message = {
        id: Math.random().toString(),
        sender: "bot",
        text: data.response || "No recibí respuesta del asistente.",
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, botMessage]);
    } catch (error: any) {
      console.error("Error al enviar mensaje al bot:", error);
      const errorMessage: Message = {
        id: Math.random().toString(),
        sender: "bot",
        text: error.message || "Hubo un error al comunicarme con el asistente de base de datos. Por favor, asegúrate de que n8n esté corriendo.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // No renderizar si el usuario no está autenticado
  if (!isAuthenticated) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50 font-sans">
      {/* Botón flotante para abrir/cerrar */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="flex items-center justify-center w-14 h-14 rounded-full bg-zinc-900 text-white shadow-xl hover:bg-zinc-800 transition-all duration-300 transform hover:scale-105 active:scale-95 dark:bg-white dark:text-zinc-950 dark:hover:bg-zinc-100"
          title="Asistente Analítico"
        >
          <MessageSquare className="w-6 h-6" />
        </button>
      )}

      {/* Ventana de chat */}
      {isOpen && (
        <div className="flex flex-col w-96 h-[500px] rounded-2xl bg-white border border-zinc-200 shadow-2xl overflow-hidden transition-all duration-300 dark:bg-zinc-950 dark:border-zinc-800">
          
          {/* Cabecera */}
          <div className="flex items-center justify-between p-4 bg-zinc-900 text-white dark:bg-zinc-900 dark:border-b dark:border-zinc-800">
            <div className="flex items-center space-x-3">
              <div className="p-1.5 rounded-lg bg-zinc-800 text-zinc-200">
                <Bot className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">Asistente INNI</h4>
                <div className="flex items-center space-x-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                  <span className="text-[10px] text-zinc-400">Analítico local en línea</span>
                </div>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-zinc-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Historial de Mensajes */}
          <div className="flex-1 p-4 overflow-y-auto space-y-4 bg-zinc-50 dark:bg-zinc-900/50">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex items-start space-x-2.5 ${
                  msg.sender === "user" ? "flex-row-reverse space-x-reverse" : ""
                }`}
              >
                {/* Avatar */}
                <div
                  className={`p-1.5 rounded-lg ${
                    msg.sender === "user"
                      ? "bg-zinc-900 text-white dark:bg-zinc-800"
                      : "bg-zinc-200 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"
                  }`}
                >
                  {msg.sender === "user" ? (
                    <User className="w-4 h-4" />
                  ) : (
                    <Bot className="w-4 h-4 text-emerald-500" />
                  )}
                </div>

                {/* Burbuja de Texto */}
                <div
                  className={`max-w-[75%] p-3 rounded-2xl text-sm leading-relaxed shadow-sm ${
                    msg.sender === "user"
                      ? "bg-zinc-900 text-white rounded-tr-none dark:bg-zinc-800"
                      : "bg-white text-zinc-800 rounded-tl-none border border-zinc-150 dark:bg-zinc-950 dark:border-zinc-800 dark:text-zinc-200"
                  }`}
                >
                  <p className="whitespace-pre-line">{msg.text}</p>
                  <span className="block text-[9px] mt-1 text-right opacity-60">
                    {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            ))}

            {/* Animación de Carga */}
            {isLoading && (
              <div className="flex items-start space-x-2.5">
                <div className="p-1.5 rounded-lg bg-zinc-200 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
                  <Bot className="w-4 h-4 text-emerald-500" />
                </div>
                <div className="flex items-center space-x-2 p-3 rounded-2xl bg-white border border-zinc-150 rounded-tl-none dark:bg-zinc-950 dark:border-zinc-800">
                  <Loader2 className="w-4 h-4 animate-spin text-emerald-500" />
                  <span className="text-xs text-zinc-500 dark:text-zinc-400">Consultando base de datos...</span>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Formulario de Envío */}
          <form
            onSubmit={handleSend}
            className="flex items-center p-3 border-t border-zinc-200 bg-white dark:bg-zinc-950 dark:border-zinc-800"
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Pregúntame sobre ventas, parcelas, clientes..."
              className="flex-1 px-4 py-2 text-sm bg-zinc-100 border-none rounded-xl focus:outline-none focus:ring-1 focus:ring-zinc-400 dark:bg-zinc-900 dark:text-white dark:focus:ring-zinc-700"
              disabled={isLoading}
            />
            <button
              type="submit"
              disabled={!input.trim() || isLoading}
              className="ml-2 p-2 rounded-xl bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-50 transition-colors dark:bg-white dark:text-zinc-950 dark:hover:bg-zinc-100"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>

        </div>
      )}
    </div>
  );
}
